import type {
  StoreActions,
  Store,
  StoreParams,
  MenuProps,
  GameMetadata,
  VariationPopoverProps,
  NodeLimits,
  NodeKeys,
  SelectNodeReturn,
  VariationPopoverEntry,
  SelectNodeOptions,
} from 'components/move-list/store/types';
import { MoveListComponent, isCommentRow, isMovesRow } from 'components/move-list/config/types';
import { States } from 'components/move-list/state-machine/types';
import data from 'src/utils/svelte/data';
import type { ContextMenu } from 'components/move-list/components/context-menu/config/types';
import {
  getLargestAndWidestTimestamps,
  getTimestampData,
} from 'components/move-list/helpers/format-time';
import type { GameAPI } from 'chessboard/types';
import { MoveList } from 'chessboard/model/move-list/types';
// eslint-disable-next-line @typescript-eslint/no-unused-vars-experimental
import type JCE from '@chesscom/js-chess-engine';
import { getTimestampFromNode } from 'chessboard/model/move/get-timestamp-from-node';
import { getMainLineAncestorIds } from 'src/utils/get-main-line-ancestor-ids';
import { Board } from 'chessboard/model/board/types';
import { findThreefoldRepetitions } from '../helpers/find-threefold-repetitions';
import { moveListToFlatTree } from '../helpers/move-list-to-flat-tree';
import { addMainLineNodeToRows } from '../helpers/add-main-line-node-to-rows';
import { isMatchingNode } from '../helpers/is-matching-node';
import {
  moveListNodesContainsNode,
  rebuildMoveListNodesWithNode,
} from '../helpers/move-list-node-utils';
import { handleSelectedNodeChangeDOMUpdates } from '../helpers/dom/handle-selected-node-change-dom-updates';

/**
 * Create svelte store
 */
export default function createStore(
  game: GameAPI,
  {
    textType,
    baseTime,
    menuEnabled,
    moveListEl,
    scrollContainer,
    translations,
    customColorsEnabled,
    displayTimeInClockFormat,
    classificationStyle,
  }: StoreParams,
): { data: Store; actions: StoreActions } {
  const store: Store = {
    // -- move list state
    startingMoveNumber: data(1),
    moveListRows: data([]),
    currentState: data(States.NoNodeSelected),
    selectedNodeKeys: data(null),
    selectedNodeEl: data(null),
    result: data(null),
    maxTimestampWidth: data(0),
    largestTimestamp: data(0),
    nodeLimits: data({
      enabled: false,
      begin: null,
      focus: null,
      end: null,
    }),
    contextMenu: data({
      enabled: menuEnabled,
      isOpen: false,
      actions: {},
    }),
    variationPopover: data({
      isOpen: false,
      entries: [],
      selectedIndex: null,
    }),
    gameMetadata: data({
      threefoldIndexes: null,
      resultPositionInfo: null,
    }),
    moveListEl: data(moveListEl),
    scrollContainer: data(scrollContainer),
    finishedInitialLoad: data(false),

    // -- settings
    classificationStyle: data(classificationStyle),
    textType: data(textType),
    baseTime: data(baseTime),
    displayTimeInClockFormat: data(displayTimeInClockFormat),
    annotationsHidden: data(false),
    variationVisibility: data(MoveListComponent.VariationVisibility.All),
    commentsHidden: data(false),
    isActive: data(true),
    commentsEditable: data(true),
    customColorsEnabled: data(customColorsEnabled),
    translations: data(translations),
    nodeEffectOverrides: data({}),
  };

  return { data: store, actions: createActions(game, store) };
}

/**
 * Create svelte store actions
 */
function createActions(game: GameAPI, storeData: Store): StoreActions {
  const {
    moveListRows,
    currentState,
    selectedNodeKeys,
    selectedNodeEl,
    textType,
    baseTime,
    result,
    contextMenu,
    variationPopover,
    annotationsHidden,
    commentsHidden,
    variationVisibility,
    maxTimestampWidth,
    largestTimestamp,
    gameMetadata,
    isActive,
    commentsEditable,
    translations,
    nodeLimits,
    customColorsEnabled,
    startingMoveNumber,
    displayTimeInClockFormat,
    nodeEffectOverrides,
  } = storeData;
  /**
   * Set current state from state machine
   */
  function setState(newState) {
    currentState.set(newState);
  }

  /**
   * Set result
   */
  function setResult(res) {
    result.set(res === '*' ? null : res);
  }

  /**
   * Set result
   */
  function setBaseTime(time: number | undefined) {
    baseTime.set(time);
  }

  /**
   * Set move list rows array
   */
  function setMoveListRows(lines: JCE.Line[]) {
    let ignoreVariationsOption: boolean | JCE.Ids;
    switch (variationVisibility.get()) {
      case MoveListComponent.VariationVisibility.None:
        ignoreVariationsOption = true;
        break;
      case MoveListComponent.VariationVisibility.Active:
        ignoreVariationsOption = getMainLineAncestorIds(selectedNodeKeys.get()?.ids, lines) ?? true;
        break;
      case MoveListComponent.VariationVisibility.All:
      default:
        ignoreVariationsOption = false;
        break;
    }

    moveListRows.set(
      moveListToFlatTree(lines, {
        ignoreComments: commentsHidden.get(),
        ignoreVariations: ignoreVariationsOption,
      }),
    );

    refreshTimestamps(lines[0]);
  }

  /**
   * refreshes largest and widest timestamp values
   */
  function refreshTimestamps(mainline: JCE.Line): void {
    const timestampData = getLargestAndWidestTimestamps(mainline, {
      displayTimeInClockFormat: displayTimeInClockFormat.get(),
      translations: translations.get(),
    }) ?? { largestTimestamp: 0, largestWidth: 0 };

    setLargestTimestamp(timestampData.largestTimestamp);
    setMaxTimestampWidth(timestampData.largestWidth);
  }

  /**
   * Adds a mainline node to the rows without recreating all the rows
   */
  function addMainLineNode(node: JCE.Node.Model) {
    addMainLineNodeToRows(node, moveListRows);
  }

  /**
   * Update the largest timestamp and related data
   */
  function setLargestTimestamp(largest: number) {
    largestTimestamp.set(largest);
  }

  /**
   * Set max timestamp width
   */
  function setMaxTimestampWidth(width: number) {
    maxTimestampWidth.set(width);
  }

  /**
   * set the displayTimeInClockFormat setting
   */
  function setDisplayTimeInClockFormat(value: boolean, mainline: JCE.Line) {
    displayTimeInClockFormat.set(value);
    refreshTimestamps(mainline);
  }

  /**
   * Set result
   */
  function setNodeLimits(newLimits: NodeLimits) {
    nodeLimits.set({
      ...nodeLimits.get(),
      ...newLimits,
    });
  }

  /**
   * Enable node limits
   */
  function enableNodeLimits() {
    nodeLimits.set({
      ...nodeLimits.get(),
      enabled: true,
    });
  }

  /**
   * Toggle comments
   */
  function setCommentsEditable(bool: boolean) {
    commentsEditable.set(bool);
  }

  function selectNode(
    node: MoveListComponent.NodeModel,
    options?: SelectNodeOptions,
  ): SelectNodeReturn;
  function selectNode(keys: NodeKeys, options?: SelectNodeOptions): SelectNodeReturn;
  /**
   * Set selected node and update the layout if necessary
   */
  function selectNode(
    nodeOrKeys: NodeKeys | MoveListComponent.NodeModel,
    options: SelectNodeOptions = {},
  ): SelectNodeReturn {
    const previousKeys = selectedNodeKeys.get();
    const keys = !nodeOrKeys ? null : { ids: nodeOrKeys.ids, hash: nodeOrKeys.hash };
    selectedNodeKeys.set(keys);

    let returnVal: SelectNodeReturn;
    if (variationVisibility.get() !== MoveListComponent.VariationVisibility.Active) {
      returnVal = { updatedLayout: false, selectedNodeKeys: keys };
    } else {
      const rawLines = options.lines ?? game.getRawLines();
      const shouldUpdateLayout = shouldSelectedNodeChangeUpdateLayout(rawLines, keys, previousKeys);
      if (shouldUpdateLayout) {
        setMoveListRows(rawLines);
      }

      returnVal = { updatedLayout: shouldUpdateLayout, selectedNodeKeys: keys };
    }

    handleSelectedNodeChangeDOMUpdates(storeData, {
      disableScrollIfScrolledUp: options.disableScrollIfScrolledUp,
    });
    return returnVal;
  }

  /**
   * Set selected node dom element
   */
  function setSelectedNodeEl(el: HTMLElement) {
    selectedNodeEl.set(el);
  }

  /**
   * Set node display type
   */
  function setTextType(displayType: Board.MoveListDisplayTypes) {
    textType.set(displayType);
  }

  /**
   * Set whether annotations are hidden
   */
  function setAnnotationsHidden(hidden: boolean) {
    annotationsHidden.set(hidden);
  }

  /**
   * Set whether comments are hidden
   */
  function setCommentsHidden(hidden: boolean) {
    commentsHidden.set(hidden);
  }

  /**
   * Set set variation visibility
   */
  function setVariationVisibility(visibility: MoveListComponent.VariationVisibility) {
    variationVisibility.set(visibility);
  }

  /**
   * Merge the variation popover props
   */
  function setVariationPopover(props: Partial<VariationPopoverProps>) {
    const popoverProps = variationPopover.get();
    const selected = game.getSelectedNode();
    const rawLines = game.getRawLines();

    if (props.isOpen && selected) {
      const { line, move } = selected?.ids || {};
      const nextNode = rawLines[line][move + 1];

      const variationEntries: VariationPopoverEntry[] =
        nextNode?.lines?.map((lineId) => {
          const rawLine = rawLines[lineId];
          const node = rawLine[0];

          const trailingSan = node.isContinuation ? rawLine[1]?.san ?? '...' : undefined;

          return {
            move: node,
            trailingSan,
          };
        }) ?? [];

      popoverProps.entries = [{ move: nextNode }, ...variationEntries];
      popoverProps.selectedIndex = 0;
    }

    variationPopover.set({ ...popoverProps, ...props });
  }

  /**
   * Set selected popover variation
   */
  function selectPopoverMove(selectedIndex: VariationPopoverProps['selectedIndex']) {
    const popoverProps = variationPopover.get();

    variationPopover.set({ ...popoverProps, selectedIndex });
  }

  /**
   * Set context menu props
   */
  function setContextMenu(props: MenuProps) {
    contextMenu.set({ ...contextMenu.get(), ...props });
  }

  function addContextMenuActions(actions: ContextMenu.Action[]);
  function addContextMenuActions(action: ContextMenu.Action);
  /**
   * Add context menu action(s)
   */
  function addContextMenuActions(actions: ContextMenu.Action[] | ContextMenu.Action) {
    const actionList = Array.isArray(actions) ? actions : [actions];

    const newActions = actionList.reduce(
      (acc, action) => ({
        ...acc,
        [action.action]: action,
      }),
      {},
    );

    const contextMenuState = contextMenu.get();
    contextMenu.set({
      ...contextMenuState,
      actions: {
        ...contextMenuState.actions,
        ...newActions,
      },
    });
  }

  function removeContextMenuActions(actionNames: string[]);
  function removeContextMenuActions(actionName: string);
  /**
   * Remove context menu action(s)
   */
  function removeContextMenuActions(actionNames: string[] | string) {
    const excludedKeys = Array.isArray(actionNames) ? actionNames : [actionNames];

    const contextMenuState = contextMenu.get();
    const currentActions = contextMenuState.actions ?? {};

    const remainingActions = Object.keys(currentActions)
      .filter((key) => !excludedKeys.includes(key))
      .reduce((acc, actionName) => {
        acc[actionName] = currentActions[actionName];
        return acc;
      }, {});

    contextMenu.set({
      ...contextMenuState,
      actions: {
        ...remainingActions,
      },
    });
  }

  /**
   * set gameMetadata
   */
  function setGameMetadata(metadata: GameMetadata) {
    gameMetadata.set(metadata);
  }

  /**
   * set gameMetadata based on details about the game state
   */
  function setGameMetadataFromGameInfo(gameAPI: GameAPI) {
    const resultInfo: GameMetadata = {
      threefoldIndexes: null,
      resultPositionInfo: null,
    };

    const { TimeoutVsInsufficientMaterial, DrawByAgreement } =
      MoveList.Translation.GameTerminationMessages;
    const translationFns = translations.get();

    if (result.get()) {
      const mainLine = gameAPI.getLine(0);
      resultInfo.threefoldIndexes = findThreefoldRepetitions(mainLine);
      resultInfo.resultPositionInfo =
        gameAPI.getFinalPositionInfo({
          timeoutVsInsufficientMaterial: translationFns[TimeoutVsInsufficientMaterial]?.(),
          drawByAgreement: translationFns[DrawByAgreement]?.(),
        }) || null;
    }

    gameMetadata.set(resultInfo);
  }

  /**
   * Set threefold repetition indexes on gameMetadata
   */
  function setThreefoldIndexes(indexes: GameMetadata['threefoldIndexes']) {
    gameMetadata.update((metadata) => ({ ...metadata, threefoldIndexes: indexes }));
  }

  /**
   * Set threefold repetition indexes on gameMetadata
   */
  function setResultPositionInfo(info: GameMetadata['resultPositionInfo']) {
    gameMetadata.update((metadata) => ({ ...metadata, resultPositionInfo: info }));
  }

  /**
   * Sets the active status on the store
   */
  function setActiveStatus(status: boolean) {
    isActive.set(status);
  }

  /**
   * Set whether custom colors are enabled
   */
  function setCustomColorsEnabled(enabled: boolean) {
    customColorsEnabled.set(enabled);
  }

  /**
   * Sets the starting move number if it has changed
   */
  function setStartingMoveNumber(number: number) {
    if (number !== startingMoveNumber.get()) {
      startingMoveNumber.set(number);
    }
  }

  /**
   * Refreshes all references to the node in the rows and updates the largest timestamp
   */
  function refreshNode(node: JCE.Node.Model) {
    if (node.ids.line === 0) {
      const timestamp = getTimestampFromNode(node);
      if (timestamp !== null) {
        if (timestamp > largestTimestamp.get()) {
          setLargestTimestamp(timestamp);
        }

        const { width } = getTimestampData({
          timestamp,
          translations: translations.get(),
          displayTimeInClockFormat: displayTimeInClockFormat.get(),
        });

        if (width > maxTimestampWidth.get()) {
          setMaxTimestampWidth(width);
        }
      }
    }

    moveListRows.get().forEach((dataRow) => {
      const row = dataRow.get();

      if (isMovesRow(row)) {
        if (moveListNodesContainsNode(node, row.content)) {
          dataRow.set({
            ...row,
            content: rebuildMoveListNodesWithNode(node, row.content),
          });
        }
      } else if (isCommentRow(row)) {
        if (isMatchingNode(node, row.content.node)) {
          dataRow.set({
            ...row,
            content: {
              ...row.content,
              node,
            },
          });
        }
      }
    });
  }

  /**
   * Sets the node effect overrides
   */
  function setNodeEffectOverrides(overrides: MoveListComponent.NodeEffectOverridesCollection) {
    nodeEffectOverrides.set(overrides);
  }

  /**
   * sets the classificationStyle setting
   */
  function setClassificationStyle(
    style: Board.MoveListClassificationStyles = Board.MoveListClassificationStyles.Default,
  ) {
    storeData.classificationStyle.set(style);
  }

  return {
    setTextType,
    setState,
    setMoveListRows,
    selectNode,
    setSelectedNodeEl,
    setResult,
    setContextMenu,
    setVariationPopover,
    selectPopoverMove,
    setBaseTime,
    setAnnotationsHidden,
    setCommentsHidden,
    setVariationVisibility,
    addContextMenuActions,
    removeContextMenuActions,
    setGameMetadata,
    setGameMetadataFromGameInfo,
    setResultPositionInfo,
    setThreefoldIndexes,
    setActiveStatus,
    setNodeLimits,
    setCommentsEditable,
    enableNodeLimits,
    setCustomColorsEnabled,
    addMainLineNode,
    setStartingMoveNumber,
    setDisplayTimeInClockFormat,
    refreshNode,
    setClassificationStyle,
    setNodeEffectOverrides,
  };
}

/**
 * determine if the selected node change should update the layout
 */
function shouldSelectedNodeChangeUpdateLayout(
  lines: JCE.Line[],
  currentKeys: NodeKeys | null,
  previousKeys: NodeKeys | null,
): boolean {
  const mainLineParentIds = getMainLineAncestorIds(currentKeys?.ids, lines);
  const previousMainlineParentIds = getMainLineAncestorIds(previousKeys?.ids, lines);

  const mainLineParent = mainLineParentIds
    ? lines[mainLineParentIds.line][mainLineParentIds.move]
    : null;

  const previousMainlineParent = previousMainlineParentIds
    ? lines[previousMainlineParentIds.line][previousMainlineParentIds.move]
    : null;

  const isNewMainlineParent = !isMatchingNode(mainLineParent, previousMainlineParent);
  const parentsHaveLines =
    (mainLineParent?.lines?.length ?? 0) > 0 || (previousMainlineParent?.lines?.length ?? 0) > 0;

  return isNewMainlineParent && parentsHaveLines;
}
